import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useAdminPricingRules,
  useCreatePricingRule,
  useUpdatePricingRule,
  useDeletePricingRule,
} from '@/api/hooks/useAdminPricing';
import { useDashboardSummary } from '@/api/hooks/useAdmin';
import { useAdminSports } from '@/api/hooks/useAdminSports';
import { useAdminUnits } from '@/api/hooks/useAdminUnits';
import { formatCurrency } from '@/lib/format';
import { pickLocaleValue } from '@/lib/i18n-pick';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, AlertTriangle, MapPin } from 'lucide-react';
import type { AdminPricingRule, PricingConflict } from '@/api/types';

// Presets first, then individual days. The backend's PricingService
// already matches on DayOfWeek.name() (see isDayTypeMatch), so the
// individual-day values below require no backend change.
const DAY_PRESETS: Array<{ value: string; label: string }> = [
  { value: 'ALL', label: 'All days' },
  { value: 'WEEKDAY', label: 'Weekdays (Mon–Fri)' },
  { value: 'WEEKEND', label: 'Weekend (Sat–Sun)' },
];
const DAY_INDIVIDUAL: Array<{ value: string; label: string }> = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];
const DAY_LABELS: Record<string, string> = Object.fromEntries(
  [...DAY_PRESETS, ...DAY_INDIVIDUAL].map((o) => [o.value, o.label]),
);

const TIME_TYPES = ['ALL', 'PEAK', 'OFF_PEAK', 'MORNING', 'AFTERNOON', 'EVENING', 'LATE_NIGHT'];
const SEASONS = ['ALL', 'WINTER', 'SUMMER', 'SHOULDER'];

// `customerSelectsUnit` is derived from each sport's bookingConfig JSONB,
// returned by the /admin/sports endpoint. See V77 / V79 seed migrations
// for the source-of-truth values per sport.
function readCustomerSelectsUnit(bookingConfig: Record<string, unknown> | undefined): boolean {
  return bookingConfig?.customerSelectsUnit === true;
}

const schema = z.object({
  name: z.string().min(1),
  sportCode: z.string().min(1),
  locationId: z.string().min(1),
  unitId: z.string().optional(),
  dayType: z.string().min(1),
  timeType: z.string().min(1),
  season: z.string().default('ALL'),
  pricePerHour: z.coerce.number().positive(),
  perPersonRate: z.coerce.number().positive().optional(),
  effectiveDate: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const [locationFilter, setLocationFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AdminPricingRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminPricingRule | null>(null);
  const [conflicts, setConflicts] = useState<PricingConflict[]>([]);

  const { data: dashboardSummary } = useDashboardSummary();
  const locations = dashboardSummary?.locationBreakdown ?? [];
  const { data: tenantSports } = useAdminSports();
  const sports = tenantSports ?? [];

  const { data, isLoading } = useAdminPricingRules({
    locationId: locationFilter !== 'all' ? locationFilter : undefined,
    sportCode: sportFilter !== 'all' ? sportFilter : undefined,
  });
  const rules = data?.rules ?? [];

  const createMutation = useCreatePricingRule();
  const updateMutation = useUpdatePricingRule();
  const deleteMutation = useDeletePricingRule();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema) as never,
    defaultValues: {
      sportCode: 'GOLF_SIM',
      dayType: 'ALL',
      timeType: 'ALL',
      season: 'ALL',
    },
  });

  const sportCode = watch('sportCode');
  const formLocationId = watch('locationId');
  const dayType = watch('dayType');
  const timeType = watch('timeType');
  const season = watch('season');

  // Fetch units for the selected sport (for the unit picker in the dialog)
  const { data: unitsData } = useAdminUnits(undefined, sportCode);

  // Multi-unit selection state. `['ALL']` is the sentinel for "applies to
  // every unit of this sport" (backend stores unit_id=null). A non-empty
  // list of real UUIDs produces one pricing rule per unit on submit — no
  // schema change needed, just fan-out on the write path.
  const ALL_UNITS = 'ALL' as const;
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([ALL_UNITS]);

  // Units at the selected location AND sport — if no location is picked
  // yet, fall back to all units for the sport.
  const dialogUnits = (unitsData?.units ?? []).filter(
    (u) => !formLocationId || u.locationId === formLocationId,
  );

  // Collapse selection to ALL if the user unchecked everything, or if the
  // sport / location change left dangling unit IDs that no longer apply.
  const pruneSelection = (ids: string[]): string[] => {
    if (ids.length === 0) return [ALL_UNITS];
    const valid = ids.filter(
      (id) => id === ALL_UNITS || dialogUnits.some((u) => u.id === id),
    );
    return valid.length === 0 ? [ALL_UNITS] : valid;
  };

  const toggleUnit = (id: string) => {
    setSelectedUnitIds((prev) => {
      if (id === ALL_UNITS) {
        // Actually toggle ALL — the previous implementation unconditionally
        // returned [ALL_UNITS], which meant unchecking "All Units" left the
        // individual checkboxes permanently disabled. When ALL is on,
        // clicking it drops to an empty selection (individual rows become
        // clickable); when it's off, clicking it re-selects ALL and
        // implicitly clears any individual picks.
        return prev.includes(ALL_UNITS) ? [] : [ALL_UNITS];
      }
      const withoutAll = prev.filter((x) => x !== ALL_UNITS);
      if (withoutAll.includes(id)) {
        // Unchecking an individual unit. Leave the list empty rather than
        // snapping back to ALL — that snap-back is why the dialog felt
        // stuck to the operator. `pruneSelection` still coerces empty to
        // ALL at submit time, so a blank state is safe but no longer
        // self-correcting during editing.
        return withoutAll.filter((x) => x !== id);
      }
      return [...withoutAll, id];
    });
  };

  const openCreate = () => {
    setEditingRule(null);
    setConflicts([]);
    setSelectedUnitIds([ALL_UNITS]);
    reset({ sportCode: 'GOLF_SIM', locationId: locations[0]?.locationId || '', dayType: 'ALL', timeType: 'ALL', season: 'ALL' });
    setDialogOpen(true);
  };

  const openEdit = (rule: AdminPricingRule) => {
    setEditingRule(rule);
    setConflicts([]);
    setSelectedUnitIds(rule.unitId ? [rule.unitId] : [ALL_UNITS]);
    reset({
      name: rule.name,
      sportCode: rule.sportCode,
      locationId: rule.locationId || undefined,
      unitId: rule.unitId || undefined,
      dayType: rule.dayType,
      timeType: rule.timeType,
      season: rule.season,
      pricePerHour: rule.pricePerHour,
      perPersonRate: rule.perPersonRate || undefined,
      effectiveDate: rule.effectiveDate || undefined,
    });
    setDialogOpen(true);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const basePayload = {
        ...data,
        perPersonRate: data.perPersonRate || undefined,
        effectiveDate: data.effectiveDate || undefined,
      };
      const activeSelection = pruneSelection(selectedUnitIds);
      const targetUnitIds: Array<string | undefined> =
        activeSelection[0] === ALL_UNITS ? [undefined] : activeSelection;
      const nameBase = data.name;
      const showUnitSuffix = targetUnitIds.length > 1;

      const allConflicts: PricingConflict[] = [];

      if (editingRule) {
        // Edit mode: update the existing rule against the first selection,
        // then fan new rules out for any additional units. Preserves the
        // original row when the selection still includes its unit.
        const originalUnitId = editingRule.unitId;
        const preferredFirst =
          originalUnitId && targetUnitIds.some((u) => u === originalUnitId)
            ? originalUnitId
            : targetUnitIds[0];
        const rest = targetUnitIds.filter((u) => u !== preferredFirst);

        const updateRes = await updateMutation.mutateAsync({
          id: editingRule.id,
          ...basePayload,
          unitId: preferredFirst,
        });
        if (updateRes.conflicts) allConflicts.push(...updateRes.conflicts);

        for (const unitId of rest) {
          const label = unitId
            ? dialogUnits.find((u) => u.id === unitId)?.name
            : undefined;
          const createRes = await createMutation.mutateAsync({
            ...basePayload,
            unitId,
            name: label ? `${nameBase} — ${label}` : nameBase,
          });
          if (createRes.conflicts) allConflicts.push(...createRes.conflicts);
        }
      } else {
        // Create mode: one POST per selected unit.
        for (const unitId of targetUnitIds) {
          const label = unitId
            ? dialogUnits.find((u) => u.id === unitId)?.name
            : undefined;
          const createRes = await createMutation.mutateAsync({
            ...basePayload,
            unitId,
            name: showUnitSuffix && label ? `${nameBase} — ${label}` : nameBase,
          });
          if (createRes.conflicts) allConflicts.push(...createRes.conflicts);
        }
      }

      if (allConflicts.length > 0) {
        setConflicts(allConflicts);
        toast.warning(t('admin.pricing_conflicts_detected'));
      } else {
        toast.success(editingRule ? t('admin.pricing_updated') : t('admin.pricing_created'));
        setDialogOpen(false);
      }
    } catch (err: unknown) {
      const appErr = err as { message?: string };
      toast.error(appErr.message || t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t('admin.pricing_deleted'));
    } catch {
      toast.error(t('common.error'));
    }
    setDeleteTarget(null);
  };

  const locationLabel =
    locationFilter === 'all'
      ? t('admin.all_locations')
      : locations.find((l) => l.locationId === locationFilter)?.locationName ?? t('admin.all_locations');

  const filterMatchedSport = sports.find((s) => s.code === sportFilter);
  const sportFilterLabel =
    sportFilter === 'all'
      ? t('admin.all_sports')
      : filterMatchedSport
          ? t(`sport_${filterMatchedSport.code}`, { defaultValue: pickLocaleValue(filterMatchedSport.name, i18n.language) })
          : sportFilter;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('admin.pricing')}</h1>
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t('admin.add_rule')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          {locations.length > 1 && (
            <Select value={locationFilter} onValueChange={(v) => setLocationFilter(v ?? 'all')}>
              <SelectTrigger>
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm">{locationLabel}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.all_locations')}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.locationId} value={loc.locationId}>{loc.locationName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={sportFilter} onValueChange={(v) => setSportFilter(v ?? 'all')}>
            <SelectTrigger>
              <span className="text-sm">{sportFilterLabel}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.all_sports')}</SelectItem>
              {sports.map((s) => (
                <SelectItem key={s.code} value={s.code}>{t(`sport_${s.code}`, { defaultValue: pickLocaleValue(s.name, i18n.language) })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Rules table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : rules.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {t('admin.no_pricing_rules')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">{t('admin.col_name')}</TableHead>
                    <TableHead className="min-w-[120px]">{t('admin.col_sport')}</TableHead>
                    <TableHead className="hidden sm:table-cell min-w-[80px]">{t('admin.col_unit')}</TableHead>
                    <TableHead className="min-w-[80px]">{t('admin.col_rate')}</TableHead>
                    <TableHead className="hidden md:table-cell min-w-[80px]">{t('admin.col_day_type')}</TableHead>
                    <TableHead className="hidden md:table-cell min-w-[80px]">{t('admin.col_time_type')}</TableHead>
                    <TableHead className="hidden lg:table-cell min-w-[70px]">{t('admin.col_season')}</TableHead>
                    <TableHead className="hidden lg:table-cell min-w-[70px]">{t('admin.col_specificity')}</TableHead>
                    <TableHead className="min-w-[100px]">{t('admin.col_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{rule.name}</span>
                          {rule.effectiveDate && (
                            <Badge variant="outline" className="text-[10px] border-amber-200 bg-amber-50 text-amber-800">
                              {rule.effectiveDate}
                            </Badge>
                          )}
                        </div>
                        {rule.locationName && (
                          <div className="text-xs text-muted-foreground">{rule.locationName}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {t(`sport_${rule.sportCode}`, { defaultValue: rule.sportName })}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                        {rule.unitName || t('admin.all_units')}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatCurrency(rule.pricePerHour, rule.currency, i18n.language)}/hr
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="secondary" className="text-xs">{rule.dayType}</Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="secondary" className="text-xs">{rule.timeType}</Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                        {rule.season}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-center text-sm">
                        {rule.specificityScore}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(rule)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(rule)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? t('admin.edit_rule') : t('admin.add_rule')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label>{t('admin.col_name')}</Label>
              <Input {...register('name')} className="mt-1" />
              {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
            </div>

            {/* Location */}
            <div>
              <Label>{t('admin.label_location')}</Label>
              <Select value={formLocationId || ''} onValueChange={(v) => setValue('locationId', v ?? '')}>
                <SelectTrigger className="mt-1">
                  <span className="text-sm">
                    {locations.find((l) => l.locationId === formLocationId)?.locationName ?? t('admin.all_locations')}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.locationId} value={loc.locationId}>{loc.locationName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t('admin.col_sport')}</Label>
                <Select value={sportCode} onValueChange={(v) => setValue('sportCode', v ?? '')}>
                  <SelectTrigger className="mt-1">
                    <span className="text-sm">
                      {(() => {
                        const s = sports.find((x) => x.code === sportCode);
                        return s ? t(`sport_${s.code}`, { defaultValue: pickLocaleValue(s.name, i18n.language) }) : sportCode;
                      })()}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {sports.map((s) => (
                      <SelectItem key={s.code} value={s.code}>
                        {t(`sport_${s.code}`, { defaultValue: pickLocaleValue(s.name, i18n.language) })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin.col_rate')}</Label>
                <Input type="number" step="0.01" min="0" {...register('pricePerHour')} className="mt-1" />
                {errors.pricePerHour && <p className="mt-1 text-xs text-destructive">{errors.pricePerHour.message}</p>}
              </div>
            </div>

            {/* Unit selector — multi-select. Picking specific units creates
                one pricing rule per unit on submit (no schema change). */}
            {readCustomerSelectsUnit(sports.find((s) => s.code === sportCode)?.bookingConfig) && dialogUnits.length > 0 && (
              <div>
                <Label>{t('admin.col_unit')}</Label>
                <div className="mt-1 space-y-1.5 rounded-md border border-neutral-200 bg-neutral-50/60 p-3 text-sm">
                  <label className="flex items-center gap-2 font-medium">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[#C2713A]"
                      checked={selectedUnitIds.includes(ALL_UNITS)}
                      onChange={() => toggleUnit(ALL_UNITS)}
                    />
                    {t('admin.all_units')}
                  </label>
                  <div className="ml-5 space-y-1">
                    {dialogUnits.map((u) => {
                      const allSelected = selectedUnitIds.includes(ALL_UNITS);
                      return (
                        <label
                          key={u.id}
                          className={
                            'flex items-center gap-2 ' +
                            (allSelected ? 'text-neutral-400' : 'text-neutral-700')
                          }
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[#C2713A]"
                            checked={!allSelected && selectedUnitIds.includes(u.id)}
                            disabled={allSelected}
                            onChange={() => toggleUnit(u.id)}
                          />
                          {u.name}
                          <span className="text-xs text-neutral-400">— {u.locationName}</span>
                        </label>
                      );
                    })}
                  </div>
                  {selectedUnitIds.length > 1 && selectedUnitIds[0] !== ALL_UNITS && (
                    <p className="mt-2 border-t border-neutral-200 pt-2 text-xs text-neutral-500">
                      A separate rule will be created for each selected unit.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label>{t('admin.col_day_type')}</Label>
                <Select value={dayType} onValueChange={(v) => setValue('dayType', v ?? '')}>
                  <SelectTrigger className="mt-1">
                    <span className="text-sm">{DAY_LABELS[dayType] ?? dayType}</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Presets</SelectLabel>
                      {DAY_PRESETS.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Individual days</SelectLabel>
                      {DAY_INDIVIDUAL.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin.col_time_type')}</Label>
                <Select value={timeType} onValueChange={(v) => setValue('timeType', v ?? '')}>
                  <SelectTrigger className="mt-1"><span className="text-sm">{timeType}</span></SelectTrigger>
                  <SelectContent>
                    {TIME_TYPES.map((tt) => <SelectItem key={tt} value={tt}>{tt}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin.col_season')}</Label>
                <Select value={season} onValueChange={(v) => setValue('season', v ?? '')}>
                  <SelectTrigger className="mt-1"><span className="text-sm">{season}</span></SelectTrigger>
                  <SelectContent>
                    {SEASONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date override — leverages the existing effective_date
                column (V25). Single-day scope: setting it to, say,
                2026-12-25 makes this rule match only on Dec 25 and
                carry the +100 specificity bonus so it dominates every
                day/time/season rule. Leave blank for an evergreen rule. */}
            <div className="rounded-md border border-dashed border-neutral-200 bg-neutral-50/50 p-3">
              <Label htmlFor="effective-date" className="text-sm">
                Date override <span className="text-xs font-normal text-neutral-400">(optional)</span>
              </Label>
              <Input
                id="effective-date"
                type="date"
                {...register('effectiveDate')}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-neutral-500">
                Pin this rule to a single calendar date (holidays, special events). Date-scoped rules beat every day-of-week and time-of-day rule.
              </p>
            </div>

            {/* Conflict warnings */}
            {conflicts.length > 0 && (
              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-yellow-800">
                  <AlertTriangle className="h-4 w-4" />
                  {t('admin.pricing_conflicts_detected')}
                </div>
                {conflicts.map((c, i) => (
                  <p key={i} className="text-xs text-yellow-700">{c.overlapDescription}</p>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {editingRule ? t('common.save') : t('admin.add_rule')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('admin.delete_rule')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('admin.delete_rule_confirm', { name: deleteTarget?.name })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {t('admin.delete_rule')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
